import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import LabeledBox from '../../special/LabeledBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useEffect, useState } from 'react';
import TypeNumber from './TypeNumber';
import uuid from 'react-uuid';
import TypeDate from './TypeDate';
import TypeTime from './TypeTime';
import TodayIcon from '@mui/icons-material/Today';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import DateTimeLabel from '../../label/DateTimeLabel';
import { updateOrCreate } from '../../special/updateOrCreate';
import DeleteIcon from '@mui/icons-material/Delete';

export const TypeReminder = ({
  updateValidationOnChange,
  dataCollection,
  field,
  size,
  errors,
}) => {
  const { t } = useTranslation();

  function handleAdd() {
    const defaultReminder = {
      remindAt: null,
      uuid: uuid(),
      type: field.relativeOnly ? 'minutesBefore' : 'dateTime',
      value: field.relativeOnly ? 30 : 5,
    };

    var newData = [];
    if (dataCollection[field.key]) newData = [...dataCollection[field.key]];

    updateValidationOnChange(field.key, [...newData, defaultReminder]);
  }

  // date & time
  const [date, setDate] = useState(
    field.dateKey ? dataCollection[field.dateKey] : null
  );
  const [time, setTime] = useState(
    field.timeKey ? dataCollection[field.timeKey] : null
  );
  const [dateTime, setDateTime] = useState(
    field.dateTimeKey ? dataCollection[field.dateTimeKey] : null
  );

  useEffect(() => {
    setDate(field.dateKey ? dataCollection[field.dateKey] : null);
    setTime(field.timeKey ? dataCollection[field.timeKey] : null);
    setDateTime(field.dateTimeKey ? dataCollection[field.dateTimeKey] : null);
  }, [
    dataCollection[field.dateKey],
    dataCollection[field.timeKey],
    dataCollection[field.dateTimeKey],
  ]);

  // handle changes
  function handleChange(reminder) {
    const compareKey = reminder.id ? 'id' : 'uuid';

    const newReminders = updateOrCreate(
      dataCollection[field.key],
      reminder,
      false,
      compareKey
    );

    updateValidationOnChange(field.key, newReminders);
  }

  function handleDelete(reminder) {
    const compareKey = reminder.id ? 'id' : 'uuid';

    const newReminders = dataCollection[field.key].filter(
      (obj) => obj[compareKey] !== reminder[compareKey]
    );
    updateValidationOnChange(field.key, newReminders);
  }

  return (
    <LabeledBox
      label={field.label}
      size={size}
      sx={{
        padding: '10px 0px',
        cursor: 'unset!important',
        transition: 'height 0.5s ease',
      }}
    >
      <Box className="overflow-hidden rounded-lg">
        <Box className="flex flex-col gap-1">
          {dataCollection[field.key] &&
            dataCollection[field.key].map((reminder) => (
              <Reminder
                key={reminder.id ? reminder.id : reminder.uuid}
                reminder={reminder}
                date={date}
                time={time}
                dateTime={dateTime}
                onChange={handleChange}
                onDelete={handleDelete}
                field={field}
                dataCollection={dataCollection}
              />
            ))}
        </Box>
        <Box className="pt-2">
          <Button
            className="flex items-center gap-4"
            color="secondary"
            onClick={handleAdd}
            sx={{
              paddingLeft: '5px',
            }}
          >
            <AddCircleIcon color="success" />
            {field.addLabel || t('Add')}
          </Button>
        </Box>
      </Box>
    </LabeledBox>
  );
};

const typeKeys = {
  daysBefore: 'days',
  hoursBefore: 'hours',
  minutesBefore: 'minutes',
};

const Reminder = ({
  reminder,
  date,
  time,
  dateTime,
  onChange,
  onDelete,
  field,
}) => {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState(reminder.type || '');
  const [values, setValues] = useState({ [reminder.type]: reminder.value });

  useEffect(() => {
    if (!reminder.isAutomatic) return;
    setSelectedType(reminder.type || '');
    setValues({ [reminder.type]: reminder.value });
  }, [reminder.type, reminder.value, reminder.isAutomatic]);

  function calculateRemindAt(type, data) {
    if (type === 'dateTime') {
      if (!data.date || !data.time) return null;
      const dataTime = moment(data.time).format('HH:mm');
      return moment(`${data.date} ${dataTime}`, 'YYYY-MM-DD HH:mm');
    }

    const dataTime = moment(time).format('HH:mm');
    const value = data[type];
    const key = typeKeys[type];
    if (!key || !value) return null;

    const formattedDate = moment(date).format('YYYY-MM-DD');
    const dateTimeMoment = dateTime
      ? moment(dateTime)
      : moment(`${formattedDate} ${dataTime}`, 'YYYY-MM-DD HH:mm');

    if (!dateTimeMoment.isValid()) return null;

    return dateTimeMoment.clone().subtract(value, key);
  }

  function updateValues(key, value) {
    const newValues = { ...values, [key]: value };
    setValues(newValues);

    const newMoment = calculateRemindAt(selectedType, newValues);
    if (newMoment && newMoment.isValid()) {
      onChange({
        ...reminder,
        remindAt: newMoment.toISOString(),
        type: selectedType,
        value: newValues[selectedType],
      });
    }
  }

  useEffect(() => {
    // Auto reminders are managed by NewMeeting's handleChange — don't recalculate here
    if (reminder.isAutomatic) return;
    // Don't recalculate for dateTime type — it uses its own local date/time
    if (selectedType === 'dateTime') return;

    const newMoment = calculateRemindAt(selectedType, values);
    if (!newMoment || !newMoment.isValid()) return;

    const newRemindAt = newMoment.toISOString();
    const existingRemindAt =
      reminder.remindAt && moment(reminder.remindAt).isValid()
        ? moment(reminder.remindAt).toISOString()
        : null;

    if (newRemindAt !== existingRemindAt) {
      onChange({
        ...reminder,
        remindAt: newRemindAt,
        type: selectedType,
        value: values[selectedType],
      });
    }
  }, [date, time, dateTime]);

  const types = {
    daysBefore: {
      label: t('days before'),
      field: (
        <TypeNumber
          dataCollection={values}
          updateValidationOnChange={updateValues}
          field={{
            label: '',
            key: 'daysBefore',
            unit: field.size == 'small' ? t('days') : t('days before'),
          }}
          size="small"
        />
      ),
    },
    hoursBefore: {
      label: t('hours before'),
      field: (
        <TypeNumber
          dataCollection={values}
          updateValidationOnChange={updateValues}
          field={{
            label: '',
            key: 'hoursBefore',
            unit: field.size == 'small' ? t('hours') : t('hours before'),
          }}
          size="small"
        />
      ),
    },
    minutesBefore: {
      label: t('minutes before'),
      field: (
        <TypeNumber
          dataCollection={values}
          updateValidationOnChange={updateValues}
          field={{
            label: '',
            key: 'minutesBefore',
            unit: field.size == 'small' ? 'min' : t('minutes before'),
          }}
          size="small"
        />
      ),
    },
    dateTime: {
      label: t('date & time'),
      field: (
        <Box className="flex gap-4 w-full">
          <TypeDate
            dataCollection={values}
            updateValidationOnChange={updateValues}
            field={{ label: '', key: 'date' }}
            size="small"
          />
          <TypeTime
            dataCollection={values}
            updateValidationOnChange={updateValues}
            field={{ label: '', key: 'time' }}
            size="small"
          />
        </Box>
      ),
    },
  };

  const handleNewType = (event, newType) => {
    if (newType) {
      setSelectedType(newType);
      const newMoment = calculateRemindAt(newType, values);
      if (newMoment && newMoment.isValid()) {
        onChange({
          ...reminder,
          remindAt: newMoment.toISOString(),
          type: newType,
          value: values[newType],
        });
      }
    }
  };

  // filter options
  const relativeOnly = field.relativeOnly || false;
  const [showOptions, setShowOptions] = useState(relativeOnly);

  useEffect(() => {
    if (relativeOnly) {
      setShowOptions(true);
      if (!selectedType || selectedType === 'dateTime')
        setSelectedType('minutesBefore');
      return;
    }
    const hasContext = Boolean((date && time) || dateTime);
    setShowOptions(hasContext);

    if (!selectedType)
      setSelectedType(hasContext ? 'minutesBefore' : 'dateTime');
  }, [date, time, dateTime, relativeOnly]);

  return (
    <Box>
      <Box className="flex gap-2 px-2 items-center">
        {showOptions && (
          <ToggleButtonGroup
            size="small"
            value={selectedType}
            exclusive
            onChange={handleNewType}
            aria-label="Reminder Type"
          >
            <Tooltip title={t('days before')}>
              <ToggleButton value="daysBefore">
                <TodayIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('hours before')}>
              <ToggleButton value="hoursBefore">
                <QueryBuilderIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('minutes before')}>
              <ToggleButton value="minutesBefore">
                <HourglassTopIcon />
              </ToggleButton>
            </Tooltip>
            {!relativeOnly && (
              <Tooltip title={t('date & time')}>
                <ToggleButton value="dateTime">
                  <EditCalendarIcon />
                </ToggleButton>
              </Tooltip>
            )}
          </ToggleButtonGroup>
        )}
        <Box className="w-full flex flex-col">{types[selectedType]?.field}</Box>
        {field.size != 'small' && !relativeOnly && (
          <Box
            className="w-[210px] monospace hidden md:block"
            sx={{ fontWeight: 600, opacity: 0.5 }}
          >
            {reminder.remindAt && (
              <DateTimeLabel dateTime={reminder.remindAt} />
            )}
          </Box>
        )}
        <IconButton color="error" onClick={() => onDelete(reminder)}>
          <DeleteIcon />
        </IconButton>
      </Box>
      {field.size == 'small' && !relativeOnly && (
        <Box
          className="w-full monospace flex justify-end pr-[54px] pt-1"
          sx={{ fontWeight: 600, opacity: 0.5 }}
        >
          {reminder.remindAt && <DateTimeLabel dateTime={reminder.remindAt} />}
        </Box>
      )}
    </Box>
  );
};
